import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  Event,
  CalendarToday,
  AttachMoney,
  Assessment,
  InfoOutlined,
} from '@mui/icons-material';
import { DividendData } from '../services/dividendService';
import { useStockData } from '../context/StockDataContext';
import { logger } from '../utils/logger';

interface DividendDisplayProps {
  stockSymbol: string;
  stockName?: string;
  currentPrice?: number;
  showTitle?: boolean;
  maxRecords?: number;
  compact?: boolean;
}

const DividendDisplay: React.FC<DividendDisplayProps> = ({
  stockSymbol,
  stockName,
  currentPrice,
  showTitle = true,
  maxRecords = 10,
  compact = false,
}) => {
  const { isDividendLoading, getDividendData } = useStockData();
  const [dividends, setDividends] = useState<DividendData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 載入股利資料
  useEffect(() => {
    // UI-07:請求序號防競態 — 快速切換股票時,舊請求較慢 resolve 不得覆蓋新資料
    let requestId = 0;
    const loadDividendData = async () => {
      if (!stockSymbol) return;

      const currentRequestId = ++requestId;
      setLoading(true);
      setError(null);

      try {
        const data = await getDividendData(stockSymbol);
        if (currentRequestId !== requestId) return; // 已被較新的請求取代

        // 按日期排序，最新的在前(複製後再 sort — data 可能是 context 快取的共用陣列)
        const sortedData = [...data].sort((a, b) =>
          new Date(b.exDividendDate).getTime() - new Date(a.exDividendDate).getTime()
        );

        // 限制顯示筆數
        setDividends(sortedData.slice(0, maxRecords));
      } catch (error) {
        if (currentRequestId !== requestId) return;
        logger.dividend.error(`DividendDisplay failed to load data for ${stockSymbol}`, error);
        setError(error instanceof Error ? error.message : '載入股利資料失敗');
      } finally {
        if (currentRequestId === requestId) {
          setLoading(false);
        }
      }
    };

    loadDividendData();
    return () => {
      requestId++; // unmount/re-run 後,進行中的請求結果一律丟棄
    };
  }, [stockSymbol, maxRecords, getDividendData]);

  // 計算統計資料
  const calculateStats = () => {
    if (dividends.length === 0) return null;

    // 最近一年的股利總額
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const recentDividends = dividends.filter(d => 
      new Date(d.exDividendDate) >= oneYearAgo
    );
    
    const totalDividendAmount = recentDividends.reduce((sum, d) => sum + d.cashDividend, 0);
    
    // 股息殖利率計算（如果有當前價格）
    const dividendYield = currentPrice && currentPrice > 0 
      ? (totalDividendAmount / currentPrice) * 100 
      : null;

    // 最高和最低股利
    const maxDividend = Math.max(...dividends.map(d => d.cashDividend));
    const minDividend = Math.min(...dividends.map(d => d.cashDividend));

    return {
      totalDividendAmount,
      dividendYield,
      maxDividend,
      minDividend,
      recentCount: recentDividends.length,
    };
  };

  const stats = calculateStats();

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // 格式化金額(總額,2 位小數)
  const formatAmount = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  // 格式化每股股利:顯示公告的完整精度(最多 8 位小數),去除尾端零
  // 例:3.49978969 → $3.49978969、3.5 → $3.5,避免進位造成與公告不符
  const formatPerShare = (amount: number) => {
    return `$${amount.toFixed(8).replace(/\.?0+$/, '')}`;
  };

  if (loading || isDividendLoading) {
    return (
      <Card>
        <CardContent>
          {showTitle && (
            <Typography variant="h6" gutterBottom>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              股利資訊載入中...
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" height={200} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          {showTitle && (
            <Typography variant="h6" gutterBottom color="error">
              股利資訊
            </Typography>
          )}
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (dividends.length === 0) {
    return (
      <Card>
        <CardContent>
          {showTitle && (
            <Typography variant="h6" gutterBottom>
              📊 股利資訊
            </Typography>
          )}
          <Alert severity="info" sx={{ mt: 1 }}>
            暫無股利發放記錄
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {showTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Assessment sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              股利資訊
            </Typography>
            {stockName && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({stockName})
              </Typography>
            )}
          </Box>
        )}

        {/* 統計摘要 */}
        {stats && !compact && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
              gap: 2,
              mb: 2 
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  近一年股利
                </Typography>
                <Typography variant="h6" color="primary.main" fontWeight="bold">
                  {formatAmount(stats.totalDividendAmount)}
                </Typography>
              </Box>
              
              {stats.dividendYield !== null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    殖利率
                  </Typography>
                  <Chip
                    label={`${stats.dividendYield.toFixed(2)}%`}
                    color={stats.dividendYield >= 3 ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              )}
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  最高股利
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {formatPerShare(stats.maxDividend)}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  發放次數
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {dividends.length} 次
                </Typography>
              </Box>
            </Box>
            <Divider />
          </Box>
        )}

        {/* 股利明細表格 */}
        <TableContainer component={Paper} variant="outlined">
          <Table size={compact ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Event sx={{ fontSize: 16, mr: 0.5 }} />
                    除息日
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />
                    發放日
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <AttachMoney sx={{ fontSize: 16, mr: 0.5 }} />
                    股利金額
                  </Box>
                </TableCell>
                {!compact && currentPrice && (
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Tooltip title="以當前股價計算的殖利率">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                        殖利率
                        <InfoOutlined sx={{ fontSize: 12, ml: 0.5, color: 'text.secondary' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {dividends.map((dividend, index) => {
                const yieldRate = currentPrice && currentPrice > 0 
                  ? (dividend.cashDividend / currentPrice) * 100 
                  : null;
                
                return (
                  <TableRow 
                    key={`${dividend.exDividendDate}-${index}`}
                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(dividend.exDividendDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(dividend.paymentDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold" 
                        color="primary.main"
                        title="公告之每股現金股利(完整精度)"
                      >
                        {formatPerShare(dividend.cashDividend)}
                      </Typography>
                    </TableCell>
                    {!compact && currentPrice && (
                      <TableCell align="right">
                        {yieldRate !== null ? (
                          <Chip
                            label={`${yieldRate.toFixed(2)}%`}
                            size="small"
                            color={yieldRate >= 1 ? 'success' : 'default'}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            --
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {dividends.length >= maxRecords && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ mt: 1, display: 'block', textAlign: 'center' }}
          >
            僅顯示最近 {maxRecords} 筆記錄
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default DividendDisplay;
